import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Card,
  CardHeader,
  Text,
  Body1,
  Caption1,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChevronDown24Regular,
  ChevronUp24Regular,
  Code24Regular,
  Copy24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  card: {
    marginTop: '10px',
    marginBottom: '10px',
    padding: '10px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '6px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    marginTop: '10px',
  },
  jsonContainer: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: '10px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    maxHeight: '400px',
    overflowY: 'auto',
  },
  jsonPre: {
    margin: 0,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '11px',
    lineHeight: '1.6',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '10px',
    marginBottom: '10px',
  },
  metadataItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metadataLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  },
  metadataValue: {
    color: tokens.colorNeutralForeground1,
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

interface PayloadViewerProps {
  displayName?: string;
  userPrincipalName?: string;
  externalId?: string;
  payloadJson?: string;
  messageType?: string;
  defaultExpanded?: boolean;
}

export function PayloadViewer({
  displayName,
  userPrincipalName,
  externalId,
  payloadJson,
  defaultExpanded = false,
}: PayloadViewerProps) {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isCopied, setIsCopied] = useState(false);

  // Parse JSON se disponível
  let parsedPayload: any = null;
  let isValidJson = false;

  if (payloadJson) {
    try {
      parsedPayload = JSON.parse(payloadJson);
      isValidJson = true;
    } catch {
      // JSON inválido, será exibido como string
      isValidJson = false;
    }
  }

  const hasPayload = !!payloadJson;
  const hasMetadata = displayName || userPrincipalName || externalId;

  // Se não há nada para exibir, retorna null
  if (!hasPayload && !hasMetadata) {
    return null;
  }

  const handleCopy = async () => {
    if (!payloadJson) return;

    try {
      await navigator.clipboard.writeText(
        isValidJson ? JSON.stringify(parsedPayload, null, 2) : payloadJson
      );
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Card className={styles.card}>
      <CardHeader
        header={
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Code24Regular />
              <Text weight="semibold">Dados da Requisicao</Text>
            </div>
            <div className={styles.headerRight}>
              {hasPayload && (
                <Tooltip content={isCopied ? 'Copiado!' : 'Copiar JSON'} relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={isCopied ? <Checkmark24Regular /> : <Copy24Regular />}
                    onClick={handleCopy}
                  >
                    {isCopied ? 'Copiado' : 'Copiar'}
                  </Button>
                </Tooltip>
              )}
              <Button
                appearance="subtle"
                size="small"
                icon={isExpanded ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Ocultar' : 'Exibir'}
              </Button>
            </div>
          </div>
        }
      />

      {isExpanded && (
        <div className={styles.content}>
          {/* Metadata Section */}
          {hasMetadata && (
            <div className={styles.metadataGrid}>
              {displayName && (
                <div className={styles.metadataItem}>
                  <Caption1 className={styles.metadataLabel}>Nome</Caption1>
                  <Body1 className={styles.metadataValue}>{displayName}</Body1>
                </div>
              )}
              {userPrincipalName && (
                <div className={styles.metadataItem}>
                  <Caption1 className={styles.metadataLabel}>Email / Login</Caption1>
                  <Body1 className={styles.metadataValue}>{userPrincipalName}</Body1>
                </div>
              )}
              {externalId && (
                <div className={styles.metadataItem}>
                  <Caption1 className={styles.metadataLabel}>ID Externo (SIS)</Caption1>
                  <Body1 className={styles.metadataValue}>{externalId}</Body1>
                </div>
              )}
            </div>
          )}

          {/* JSON Payload Section */}
          {hasPayload ? (
            <div className={styles.jsonContainer}>
              <pre className={styles.jsonPre}>
                {isValidJson
                  ? JSON.stringify(parsedPayload, null, 2)
                  : payloadJson}
              </pre>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Code24Regular style={{ fontSize: '48px', opacity: 0.3 }} />
              <Body1>Payload não disponível para este evento</Body1>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
